// The Weight Maps — one tool per 100 lb class.
//
// Owner, 2026-09-13: "a price map like we have for 700 weights but for every
// weight class and each is a separate tool on the admin side… 100 lb classes,
// steers and heifers with toggle… all classes as long as we have the numbers to
// support."
//
// ⚠️ THIS LIST IS THE ONE PLACE A CLASS IS ADDED OR REMOVED, and it has to agree
// with three others, each pinned by weightMap.test.ts:
//   - the `p_band in (...)` guard in rpc_weight_map_today / _week (latest: migration 20260913000005),
//   - the enum on p_band in api/market.ts (CDN cache keys),
//   - a <Route> and a vercel.json rewrite per `path`.
//
// WHY 400–1000 AND NOT 300 OR 1100: measured, not chosen. A class qualifies
// when both steers and heifers average >= 4 states selling per weekday AND
// >= 3 states clearing the week view's 3-barn floor. 300s: 3.3 / 2 (steers),
// 3.4 / 0 (heifers). 1100s: <= 2.2 / <= 1. Full table in migration
// 20260913000003_rpc_weight_map.sql. Re-measure before adding one.
//
// ⚠️ 1000–1099 SITS ABOVE THE PLATFORM'S 300–999 TAXONOMY. Owner-approved for
// these maps on 2026-09-13; see the migration header.

export interface WeightMapClass {
  /** Lower bound, lb. The class is [band, band + 100). */
  band: number;
  /** "7 Wt Map" — also the rb_tools name (<= 32 chars). */
  name: string;
  /** "/7-wt-map" — also the rb_tools href and slug. */
  path: string;
  /** "700–799" */
  range: string;
  /** The prerendered document, "wt-map-7.html". */
  file: string;
}

export const WEIGHT_MAPS: readonly WeightMapClass[] = [400, 500, 600, 700, 800, 900, 1000].map(
  (band) => {
    const n = band / 100;
    return {
      band,
      name: `${n} Wt Map`,
      path: `/${n}-wt-map`,
      range: `${band}–${band + 99}`,
      file: `wt-map-${n}.html`,
    };
  },
);

export type WeightMapCattle = "Steers" | "Heifers";
export const WEIGHT_MAP_CATTLE: readonly WeightMapCattle[] = ["Steers", "Heifers"];

/** The smallest pen a map counts: 5 head under 700 lb, 10 head from 700 lb up
 *  (owner, 2026-10-19). Mirrors public.rb_map_lot_counts (migration
 *  20261019000011); weightMap.test.ts pins the two together. The maps only —
 *  the RB indexes and every other screen keep the site-wide rule. */
export function weightMapMinHead(band: number): 5 | 10 {
  return band < 700 ? 5 : 10;
}

export function weightMapFor(band: number): WeightMapClass | undefined {
  return WEIGHT_MAPS.find((m) => m.band === band);
}
